import React, { useState } from "react";
import { WebinarHost } from "../../types/models";
import HostProfileModal from "../HostProfileModal/HostProfileModal";
import "./HostPicker.scss";

type Props = {
  hosts: WebinarHost[];
  value: string;
  onChange: (hostId: string) => void;
  onHostsChange: (hosts: WebinarHost[]) => void;
};

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("");
}

const HostPicker: React.FC<Props> = ({
  hosts,
  value,
  onChange,
  onHostsChange,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<WebinarHost | null>(null);

  return (
    <div className="wf-host-picker">
      {hosts.length === 0 ? (
        <div className="wf-host-picker__empty">
          No hosts yet. Create a host profile to assign this session.
        </div>
      ) : null}
      <div className="wf-host-picker__list">
        {hosts.map((h) => {
          const active = h.id === value;
          return (
            <div
              key={h.id}
              className={`wf-host-picker__card${active ? " is-active" : ""}`}
              role="button"
              tabIndex={0}
              onClick={() => onChange(h.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") onChange(h.id);
              }}
            >
              <div className="wf-avatar">
                {h.photo ? <img src={h.photo} alt="" /> : <span>{initials(h.name)}</span>}
              </div>
              <div className="wf-host-picker__meta">
                <div className="name">{h.name}</div>
                <div className="role">{h.role}</div>
              </div>
              <button
                type="button"
                className="wf-link"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditing(h);
                  setModalOpen(true);
                }}
              >
                Edit
              </button>
            </div>
          );
        })}
        <button
          type="button"
          className="wf-host-picker__add"
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
        >
          {hosts.length === 0 ? "+ Create first host" : "+ Add new host"}
        </button>
      </div>

      <HostProfileModal
        isOpen={modalOpen}
        host={editing}
        onClose={() => setModalOpen(false)}
        onSaved={(saved) => {
          const exists = hosts.some((h) => h.id === saved.id);
          const next = exists
            ? hosts.map((h) => (h.id === saved.id ? saved : h))
            : [...hosts, saved];
          onHostsChange(next);
          onChange(saved.id);
        }}
      />
    </div>
  );
};

export default HostPicker;
